import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import clsx from "clsx";
import dayjs from "dayjs";

import Button from "components/Button";
import Input from "components/Input";
import Select from "components/Select";
import Tooltip from "components/Tooltip";
import { COLOR_BY_STATUS_FOR_MY_CARRIER } from "constants/base";
import { PERCENTAGE_LIST, PORTION_PERCENT_LIST } from "constants/haulpay-broker";
import { CARRIER_PAYMENT_SPEED } from "constants/haulpay-carrier";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { useUserFeature } from "hooks/useUserFeature";
import { IOption } from "models/Form";
import { IFactoringPaymentProfile, IPaymentSpeed } from "models/HaulPay";
import { updateCarrierProfileAPI } from "services/api/haulpay";
import { selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";
import PaymentSpeedInput from "components/PaymentSpeedInput";

interface IProps {
  data: IFactoringPaymentProfile;
  toggleInfoModal: (id: string) => void;
  send_update_email?: boolean;
  pendingApprovalTasks: string[];
  defaultPaymentSettings: IPaymentSpeed[];
}

export default function MyCarrierItem({
  data,
  toggleInfoModal,
  send_update_email = false,
  pendingApprovalTasks,
  defaultPaymentSettings
}: Readonly<IProps>) {
  const userDetails = useSelector(selectUserDetails);
  const { isBrokerSubscribed } = useUserFeature();
  const [isUpdatingCarrier, setIsUpdatingCarrier] = useState(false);
  const {
    control,
    reset,
    register,
    setValue,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<
    Partial<{
      carrier_rate: string;
      external_code: string;
      payment_speed: IOption & { amount: number };
      broker_portion: IOption;
    }>
  >();
  const paymentSpeed = useWatch({ control, name: "payment_speed" });
  const brokerPortion = useWatch({ control, name: "broker_portion" });

  const updateCarrierItem = async (formData: {
    carrier_rate: string;
    external_code: string;
    payment_speed: IOption & { amount: number };
    broker_portion: IOption;
  }) => {
    setIsUpdatingCarrier(true);
    try {
      await updateCarrierProfileAPI({
        factoring_id: userDetails.factoring_id,
        carrier_id: data.id,
        data: {
          enable_discount: false,
          external_code: formData.external_code,
          factoring_fee_split_rate: Number(formData.broker_portion.value),
          payout_days: formData.payment_speed.value
        },
        send_update_email: send_update_email
      });
      showSuccessMessage("Carrier has been updated.");
    } catch (e: any) {
      showErrorMessage("Error updating carrier.");
    } finally {
      setIsUpdatingCarrier(false);
    }
  };

  useEffect(() => {
    reset({
      payment_speed: CARRIER_PAYMENT_SPEED.find((speed) => speed.value === data.payout_days),
      broker_portion: PERCENTAGE_LIST.find(
        (percent) => Number(percent.value) === data.factoring_fee_split_rate
      ),
      external_code: data.external_code
    });
  }, []);

  useEffect(() => {
    if (paymentSpeed && brokerPortion) {
      const carrierRate = (paymentSpeed.amount * (100 - Number(brokerPortion.value))) / 100 + "%";
      setValue("carrier_rate", carrierRate);
    }
  }, [paymentSpeed, brokerPortion]);

  return (
    <form className={styles.myCarrierItemContainer} onSubmit={handleSubmit(updateCarrierItem)}>
      <ul>
        <li className="font-bold">Email</li>
        <li className="break-words text-primary">{data.payment_profile.owner_email}</li>
      </ul>
      <ul>
        <li className="font-bold">Carrier Name</li>
        <button
          type="button"
          className={clsx("text-left", isBrokerSubscribed ? "text-info" : "")}
          onClick={() => toggleInfoModal(data.id)}>
          {data.payment_profile.company_name}
        </button>
      </ul>
      <ul>
        <li className="font-bold">Phone number</li>
        <li>{data.payment_profile.owner_phone_number_cell}</li>
      </ul>
      <ul>
        <li className="font-bold">MC #</li>
        <li>{data.payment_profile.company_mc}</li>
      </ul>
      <ul>
        <li className="font-bold">DOT #</li>
        <li>{data.payment_profile.company_dot}</li>
      </ul>
      <ul>
        <li className="my-auto font-bold">Payment Speed</li>
        <li>
          <PaymentSpeedInput
            noLabel
            skip
            control={control}
            name="payment_speed"
            defaultPaymentSpeed={defaultPaymentSettings}
          />
        </li>
      </ul>
      <ul>
        <li className="font-bold">Broker Portion</li>
        <li>
          <Controller
            name="broker_portion"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                options={PORTION_PERCENT_LIST}
                value={value}
                onChange={onChange}
                btnClass="!py-1.5 !pl-1.5 !pr-4 desktop:!p-2.5"
                className="w-full text-xxs desktop:text-sm"
                arrowClass="!right-0"
                dropdownClass="!p-2"
              />
            )}
          />
        </li>
      </ul>
      <ul>
        <li className="font-bold">Carrier Rate</li>
        <li>
          <Input
            disabled
            name="carrier_rate"
            register={register}
            inputClass="!border-icon !rounded-lg !p-1.5 desktop:!p-2.5 h-7 desktop:h-10 text-xs desktop:text-sm"
          />
        </li>
      </ul>
      <ul>
        <li className="font-bold">External Code</li>
        <li>
          <Input
            type="text"
            name="external_code"
            register={register}
            inputClass="!border-icon !rounded-lg !p-1.5 desktop:!p-2.5 h-7 desktop:h-10 text-xs desktop:text-sm"
          />
        </li>
      </ul>
      <ul>
        <li className="font-bold">status</li>
        <li className="flex flex-col text-xxs">
          <Tooltip
            message={
              pendingApprovalTasks.length === 0 ? (
                <span className="text-sm">No Pending Approval Tasks</span>
              ) : (
                <div className="text-sm text-center">
                  <p>Pending Approval Tasks</p>
                  <ul className="mt-1.5">
                    {pendingApprovalTasks.map((task) => (
                      <li key={task}>- {task}</li>
                    ))}
                  </ul>
                </div>
              )
            }>
            <ul
              className={clsx(
                "flex flex-col gap-0.5 items-start !bg-transparent !border-none",
                COLOR_BY_STATUS_FOR_MY_CARRIER[data.payment_profile.status]
              )}>
              <li className="uppercase">{data.payment_profile.status}</li>
              <li className="!text-text">{dayjs(data.status_date).format("M/D/YYYY HH:mm A")}</li>
            </ul>
          </Tooltip>
        </li>
      </ul>
      <ul>
        <li className="font-bold">Action</li>
        <li>
          <Button
            type="submit"
            variant="outline"
            rounded
            size="sm"
            className="text-xs"
            disabled={isUpdatingCarrier || isSubmitting}
            loading={isSubmitting}>
            Save
          </Button>
        </li>
      </ul>
    </form>
  );
}
